import { ref } from "vue";
import de from "../locales/de.json";
import es from "../locales/es.json";
import it from "../locales/it.json";
import ka from "../locales/ka.json";
import nl from "../locales/nl.json";
import pt from "../locales/pt.json";

export type Locale = "en" | "de" | "es" | "it" | "ka" | "nl" | "pt";

const STORAGE_KEY = "rasenschach.locale";

// English strings double as translation keys, so "en" needs no dictionary:
// t() falls back to the key itself.
const dictionaries = ref<Partial<Record<Locale, Record<string, string>>>>({ de, es, it, ka, nl, pt });

function isLocale(value: string | null): value is Locale {
  return value === "en" || value === "de" || value === "es" || value === "it" || value === "ka" || value === "nl" || value === "pt";
}

const locale = ref<Locale>("en");
let urlLocale: Locale | null = null;
try {
  const requested = new URLSearchParams(window.location.search).get("lang");
  if (isLocale(requested)) {
    urlLocale = requested;
  }

  const saved = localStorage.getItem(STORAGE_KEY);
  if (urlLocale) {
    // A shared link must open in the sender's language, even when the recipient
    // has a different locale stored from an earlier visit.
    locale.value = urlLocale;
    localStorage.setItem(STORAGE_KEY, urlLocale);
  } else if (isLocale(saved)) {
    locale.value = saved;
  } else {
    const browserLocale = navigator.language?.toLowerCase();
    if (browserLocale?.startsWith("de")) locale.value = "de";
    else if (browserLocale?.startsWith("es")) locale.value = "es";
    else if (browserLocale?.startsWith("it")) locale.value = "it";
    else if (browserLocale?.startsWith("ka")) locale.value = "ka";
    else if (browserLocale?.startsWith("nl")) locale.value = "nl";
    else if (browserLocale?.startsWith("pt")) locale.value = "pt";
  }
} catch { /* storage unavailable (e.g. blocked) — keep default */ }

if (typeof document !== "undefined") document.documentElement.lang = locale.value;

async function loadLocale(l: Locale): Promise<void> {
  if (l === "en" || dictionaries.value[l]) return;
  throw new Error(`Missing locale dictionary: ${l}`);
}

void loadLocale(locale.value);

function t(key: string, params?: Record<string, string | number>): string {
  let s = locale.value === "en" ? key : dictionaries.value[locale.value]?.[key] ?? key;
  if (params) {
    for (const [name, value] of Object.entries(params)) {
      s = s.replaceAll(`{${name}}`, String(value));
    }
  }
  return s;
}

async function setLocale(l: Locale): Promise<void> {
  await loadLocale(l);
  locale.value = l;
  if (typeof document !== "undefined") document.documentElement.lang = l;
  try {
    localStorage.setItem(STORAGE_KEY, l);
  } catch { /* storage unavailable — non-fatal */ }
}

export function useI18n() {
  return { t, locale, setLocale };
}
